import { useState, useMemo, useCallback } from 'react';
import { DateRange } from 'react-day-picker';
import { Outlet, useNavigate, useSearch } from '@tanstack/react-router';
import Page from '@/components/page/Page';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Card } from '@/components/ui/card';
import { DateRangePicker } from '@/components/common/date-pickers/date-range-picker';
import { Button } from '@/components/ui/button';
import PaymentsTable from './components/PaymentsTable';
import TransactionsTable from './components/TransactionsTable';
import { format, subDays, parseISO } from 'date-fns';
import { ArrowsClockwiseIcon } from '@phosphor-icons/react';
import { usePaymentsList } from '@/api/rpc/hooks/finance/usePayments';
import {
  useTransactionsList,
  useTransactionsSync,
} from '@/api/rpc/hooks/finance/useTransactions';

// Payment status options from PaymentStatus enum in backend
const PAYMENT_STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'in_review', label: 'In Review' },
  { value: 'approved', label: 'Approved' },
  { value: 'processing', label: 'Processing' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'pending', label: 'Pending' },
  { value: 'paid', label: 'Paid' },
  { value: 'failed', label: 'Failed' },
  { value: 'canceled', label: 'Canceled' },
];

export default function PaymentsPage() {
  const navigate = useNavigate();

  // Get search params from URL
  const searchParams = useSearch({ strict: false }) as {
    tab?: 'payments' | 'transactions';
    status?: string;
    dateFrom?: string;
    dateTo?: string;
  };

  // Derive state from URL params
  const activeTab = searchParams.tab || 'payments';
  const statusFilter = searchParams.status || '';

  // Parse date range from URL params
  const dateRange = useMemo<DateRange | undefined>(() => {
    if (!searchParams.dateFrom && !searchParams.dateTo) return undefined;
    return {
      from: searchParams.dateFrom ? parseISO(searchParams.dateFrom) : undefined,
      to: searchParams.dateTo ? parseISO(searchParams.dateTo) : undefined,
    };
  }, [searchParams.dateFrom, searchParams.dateTo]);

  // Pagination state (kept local as it resets on filter changes)
  const [paymentsPagination, setPaymentsPagination] = useState({
    pageIndex: 0,
    pageSize: 50,
  });

  // Update URL params helper
  const updateSearchParams = useCallback(
    (updates: {
      tab?: 'payments' | 'transactions';
      status?: string;
      dateFrom?: string;
      dateTo?: string;
    }) => {
      navigate({
        to: '/accounts-payable/payments',
        search: {
          tab: updates.tab ?? activeTab,
          status: updates.status !== undefined ? updates.status : statusFilter,
          dateFrom:
            updates.dateFrom !== undefined
              ? updates.dateFrom
              : searchParams.dateFrom,
          dateTo:
            updates.dateTo !== undefined ? updates.dateTo : searchParams.dateTo,
        },
        replace: true,
      });
    },
    [navigate, activeTab, statusFilter, searchParams],
  );

  // Build filters for payments including pagination
  const paymentFilters = useMemo(
    () => ({
      dateFrom: dateRange?.from
        ? format(dateRange.from, 'yyyy-MM-dd')
        : undefined,
      dateTo: dateRange?.to ? format(dateRange.to, 'yyyy-MM-dd') : undefined,
      status: statusFilter || undefined,
      limit: paymentsPagination.pageSize,
      offset: paymentsPagination.pageIndex * paymentsPagination.pageSize,
    }),
    [dateRange, statusFilter, paymentsPagination],
  );

  // Build filters for transactions
  const transactionFilters = useMemo(
    () => ({
      dateFrom: dateRange?.from
        ? format(dateRange.from, 'yyyy-MM-dd')
        : undefined,
      dateTo: dateRange?.to ? format(dateRange.to, 'yyyy-MM-dd') : undefined,
      source: 'plaid' as const,
    }),
    [dateRange],
  );

  const {
    payments,
    total: paymentsTotal,
    isLoading: isLoadingPayments,
  } = usePaymentsList(paymentFilters);

  const {
    transactions,
    total: transactionsTotal,
    lastSyncTime,
    isLoading: isLoadingTransactions,
  } = useTransactionsList(transactionFilters);

  const { mutate: syncTransactions, isPending: isSyncing } =
    useTransactionsSync();

  // Reset pagination when filters change
  const handleFilterChange = useCallback(() => {
    setPaymentsPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  const handleDateRangeChange = useCallback(
    (range: DateRange | undefined) => {
      updateSearchParams({
        dateFrom: range?.from ? format(range.from, 'yyyy-MM-dd') : undefined,
        dateTo: range?.to ? format(range.to, 'yyyy-MM-dd') : undefined,
      });
      handleFilterChange();
    },
    [updateSearchParams, handleFilterChange],
  );

  const handleStatusChange = useCallback(
    (status: string) => {
      updateSearchParams({
        status: status || undefined,
      });
      handleFilterChange();
    },
    [updateSearchParams, handleFilterChange],
  );

  const handleTabChange = useCallback(
    (tab: string) => {
      updateSearchParams({
        tab: tab as 'payments' | 'transactions',
      });
    },
    [updateSearchParams],
  );

  const handleSync = () => {
    // Use date range if available, otherwise use last 30 days
    const from = dateRange?.from || subDays(new Date(), 30);
    const to = dateRange?.to || new Date();

    syncTransactions({
      dateFrom: format(from, 'yyyy-MM-dd'),
      dateTo: format(to, 'yyyy-MM-dd'),
    });
  };

  return (
    <Page
      title="Payments & Transactions"
      isLoading={
        activeTab === 'payments' ? isLoadingPayments : isLoadingTransactions
      }
    >
      <div className="space-y-4">
        {/* Filters */}
        <Card className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <DateRangePicker
              value={dateRange}
              onChange={handleDateRangeChange}
            />
            {activeTab === 'payments' && (
              <select
                className="h-9 rounded-md border border-input bg-background px-3 py-1 text-sm"
                value={statusFilter}
                onChange={(e) => handleStatusChange(e.target.value)}
              >
                {PAYMENT_STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            )}
            {activeTab === 'transactions' && (
              <div className="flex items-center gap-2 flex-wrap">
                {lastSyncTime && (
                  <span className="text-sm text-muted-foreground whitespace-nowrap">
                    Last sync:{' '}
                    {format(new Date(lastSyncTime), 'MM/dd/yyyy HH:mm')}
                  </span>
                )}
                <Button
                  onClick={handleSync}
                  disabled={isSyncing}
                  size="sm"
                  variant="outline"
                  className="whitespace-nowrap"
                >
                  <ArrowsClockwiseIcon
                    className={`h-4 w-4 mr-2 ${isSyncing ? 'animate-spin' : ''}`}
                  />
                  {isSyncing ? 'Syncing...' : 'Sync Transactions'}
                </Button>
              </div>
            )}
          </div>
        </Card>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={handleTabChange}>
          <TabsList>
            <TabsTrigger value="payments">
              Payments ({paymentsTotal})
            </TabsTrigger>
            <TabsTrigger value="transactions">
              Bank Transactions ({transactionsTotal})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="payments">
            <PaymentsTable
              payments={payments}
              total={paymentsTotal}
              paginationState={paymentsPagination}
              onPaginationChange={setPaymentsPagination}
              filters={paymentFilters}
            />
          </TabsContent>

          <TabsContent value="transactions">
            <TransactionsTable transactions={transactions} />
          </TabsContent>
        </Tabs>
      </div>
      <Outlet />
    </Page>
  );
}
